import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { twJoin } from "tailwind-merge";

import { BasicModelCard, RenderableModelCard } from "src/aiNode/types";
import { Icon } from "src/base-components/Icon";
import { Select, Option } from "src/base-components/Select";
import { Tooltip } from "src/design-system/Tooltip";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type ModelIconProps = {
  modelCard: BasicModelCard;
};

export const ModelIcon: React.FC<ModelIconProps> = ({ modelCard }) => {
  const iconSrc = "https://app.cp.taktile.com" + modelCard.icon_path;

  return (
    <div className="box-border h-5 w-5 rounded-[4px] border border-gray-200">
      <img
        alt={`${modelCard.name} Icon`}
        data-loc={`${modelCard.model_id}-icon`}
        src={iconSrc}
      />
    </div>
  );
};

const TaktileHostedBadge: React.FC = () => {
  return (
    <Tooltip
      body="This model is hosted by Taktile via AWS Bedrock for additional privacy. This might come with lower usage limits."
      placement="right"
      asChild
    >
      <div className="flex h-5 shrink-0 items-center rounded bg-gray-100 pl-2 pr-1">
        <span className="text-gray-600 font-inter-normal-12px">
          Taktile-hosted
        </span>
        <Icon color="text-gray-400" icon={faInfoCircle} size="2xs" />
      </div>
    </Tooltip>
  );
};

type DifferentRegionBadgeProps = {
  targetRegion: string;
};

const DifferentRegionBadge: React.FC<DifferentRegionBadgeProps> = ({
  targetRegion,
}) => {
  return (
    <Tooltip
      body={
        <div>
          <div className="mb-1">
            This model is not available in your region. Requests will be
            processed in the{" "}
            <span className="font-semibold">{targetRegion}</span> to ensure
            availability.
          </div>
          <a
            className="text-indigo-400 underline hover:text-indigo-300"
            href="https://docs.taktile.com/decision-flow-fundamentals/node-types/ai-node#model-selection"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more
          </a>
        </div>
      }
      placement="right"
      asChild
    >
      <div className="flex h-5 shrink-0 items-center rounded bg-yellow-100 px-2 py-1">
        <span className="text-yellow-700 font-inter-normal-12px">
          Processed in {targetRegion}
        </span>
      </div>
    </Tooltip>
  );
};

type ModelSelectorDropdownProps = {
  immutable: boolean;
  loading?: boolean;
  models: RenderableModelCard[];
  onSelect: (modelId: string) => void;
  selectedModelId: string;
};

// Configuration for model rerouting by region
const REGION_ROUTING_CONFIG: Record<string, { targetRegion: string }> = {
  "eu-west-2": { targetRegion: "EU" }, // UK -> EU region
  "ca-central-1": { targetRegion: "US/CA" }, // Canada -> US region
  "sa-east-1": { targetRegion: "Global" }, // Brazil -> Global region
  "ap-south-1": { targetRegion: "Global" }, // Mumbai -> Global region
  "ap-southeast-1": { targetRegion: "Global" }, // Singapore -> Global region
};

// Models that should show the rerouted badge (Claude 4.5 Sonnet and Haiku)
const REROUTED_MODELS = ["claude-4-5-sonnet", "claude-4-5-haiku"];

export const ModelSelectorDropdown: React.FC<ModelSelectorDropdownProps> = ({
  models,
  immutable,
  onSelect,
  selectedModelId,
  loading = false,
}) => {
  const { workspace } = useWorkspaceAndOrg();
  const workspaceRegion = workspace?.aws_region;
  const routingConfig =
    workspaceRegion && REGION_ROUTING_CONFIG[workspaceRegion];

  const selectedModelCard = models.find(
    (model) => model.model_id === selectedModelId,
  );

  const options: Option[] = models.map((model) => ({
    key: model.model_id,
    value: (
      <Tooltip
        body="This feature requires the AI Workbench package. Please contact your Solution Engineer to obtain access to this model."
        disabled={model.available}
        placement="bottom"
        title="Model not available"
        asChild
      >
        <div
          className="flex items-start gap-2 px-1"
          data-loc={`select-${model.model_id}`}
        >
          <div>
            <ModelIcon modelCard={model} />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-start gap-1.5">
              <span
                className={twJoin(
                  "font-inter-normal-13px",
                  model.available ? "text-gray-800" : "text-gray-500",
                )}
              >
                {model.name}
              </span>
              {model.providers?.[0] === "bedrock" && <TaktileHostedBadge />}
              {routingConfig && REROUTED_MODELS.includes(model.model_id) && (
                <DifferentRegionBadge
                  targetRegion={routingConfig.targetRegion}
                />
              )}
            </div>
            <span className="whitespace-normal break-words text-gray-500 font-inter-normal-12px">
              {model.description}
            </span>
          </div>
        </div>
      </Tooltip>
    ),
    disabled: !model.available,
    searchText: `${model.name} ${model.description}`,
  }));

  const buttonRenderer = (option?: Option) => {
    const modelCard = option
      ? models.find((m) => m.model_id === option.key)
      : selectedModelCard;

    return (
      <div className="flex w-full items-center gap-x-2 p-1">
        {modelCard && <ModelIcon modelCard={modelCard} />}
        <div
          className={twJoin(
            "min-w-0 grow truncate text-left font-inter-normal-12px",
            immutable ? "text-gray-500" : "text-gray-800",
          )}
        >
          {modelCard?.name}
        </div>
      </div>
    );
  };

  const handleChange = (value: string | null) => {
    if (value) {
      onSelect(value);
    }
  };

  return (
    <div className="w-full">
      <Select
        buttonRenderer={buttonRenderer}
        dataLoc="ai-node-model-selector"
        disabled={immutable}
        loading={loading}
        options={options}
        placeholder="Select model"
        searchable={false}
        value={selectedModelId}
        compact
        fullWidth
        onChange={handleChange}
      />
    </div>
  );
};
